import { Link, useNavigate } from 'react-router-dom'
import { useCategories, useDeleteCategory } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

export function CategoryList() {
  const navigate = useNavigate()
  const { data: categories, isLoading, error } = useCategories()
  const deleteMutation = useDeleteCategory()

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`"${name}" 템플릿 카테고리를 삭제하시겠습니까?`)) return

    try {
      await deleteMutation.mutateAsync(id)
      toast.success('템플릿 카테고리가 삭제되었습니다')
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '삭제 중 오류가 발생했습니다')
    }
  }

  if (isLoading) return <div className="text-center py-12">로딩 중...</div>
  if (error) return <div className="text-center py-12 text-red-500">에러: {error.message}</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">템플릿 카테고리</h1>
          <p className="text-caption mt-2 leading-relaxed">템플릿 카테고리 관리</p>
        </div>
        <Link to="/admin/categories/new">
          <Button>+ 새 템플릿 카테고리</Button>
        </Link>
      </div>

      <div className="bg-white rounded-lg border min-w-[800px]">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                이름
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                설명
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                순서
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                상태
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {categories?.map((cat) => (
              <tr
                key={cat.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/admin/categories/${cat.id}/view`)}
              >
                <td className="px-6 py-4 font-mono text-sm">{cat.id}</td>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{cat.name}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">{cat.description || '-'}</td>
                <td className="px-6 py-4 text-sm">{cat.listOrder}</td>
                <td className="px-6 py-4">
                  <Badge variant={cat.enabled ? 'success' : 'secondary'}>
                    {cat.enabled ? '활성' : '비활성'}
                  </Badge>
                </td>
                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/admin/categories/${cat.id}/edit`}>
                      <Button variant="ghost" size="sm">
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(cat.id, cat.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
